import { AuthProvider } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL || 'https://api-qa.nexuska.com/api';
const CSRF_COOKIE_NAME = 'admin_csrf';

interface SessionIdentity {
  id: number;
  email: string;
  fullName?: string;
  roles: string[];
}

let cachedIdentity: SessionIdentity | null = null;
let isLogoutInFlight = false;

function getCookie(name: string): string | null {
  const target = `${name}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(target));

  if (!found) {
    return null;
  }

  return decodeURIComponent(found.substring(target.length));
}

async function fetchSessionIdentity(): Promise<SessionIdentity> {
  const response = await fetch(`${API_URL}/admin/auth/session`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Not authenticated');
  }

  const identity = (await response.json()) as SessionIdentity;
  cachedIdentity = identity;
  return identity;
}

export const authProvider: AuthProvider = {
  login: async () => {
    window.location.assign(`${API_URL}/admin/auth/login`);
    return Promise.resolve();
  },

  logout: async () => {
    cachedIdentity = null;

    // React Admin may call logout after failed checkAuth; avoid a request loop
    // when there is clearly no authenticated admin cookie context.
    const csrfToken = getCookie(CSRF_COOKIE_NAME);
    if (!csrfToken || isLogoutInFlight) {
      return Promise.resolve();
    }

    isLogoutInFlight = true;
    const headers: HeadersInit = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    };

    try {
      const response = await fetch(`${API_URL}/admin/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers,
      });

      // Already unauthenticated; nothing else to do.
      if (response.status === 401 || response.status === 403) {
        return Promise.resolve();
      }

      if (response.ok) {
        const payload = await response.json();
        if (payload?.logoutUrl && typeof payload.logoutUrl === 'string') {
          window.location.assign(payload.logoutUrl);
        }
      }
    } finally {
      isLogoutInFlight = false;
    }

    return Promise.resolve();
  },

  checkAuth: async () => {
    await fetchSessionIdentity();
    return Promise.resolve();
  },

  checkError: async (error) => {
    const status = error?.status;
    if (status === 401 || status === 403) {
      cachedIdentity = null;
      return Promise.reject();
    }
    return Promise.resolve();
  },

  getIdentity: async () => {
    if (cachedIdentity) {
      return Promise.resolve({
        id: cachedIdentity.id,
        fullName: cachedIdentity.fullName || cachedIdentity.email,
      });
    }

    const identity = await fetchSessionIdentity();
    return Promise.resolve({
      id: identity.id,
      fullName: identity.fullName || identity.email,
    });
  },

  getPermissions: async () => {
    if (!cachedIdentity) {
      await fetchSessionIdentity();
    }

    if (!cachedIdentity) {
      return Promise.reject();
    }

    return Promise.resolve(cachedIdentity.roles || []);
  },
};
